import { observer } from 'owa-mobx-react';

import { AttachmentCompactView, SpecialDocViewMode } from 'owa-attachment-compact-view';
import loc, { format } from 'owa-localize';
import { getListViewColumnWidths, type PreviewWellViewState } from 'owa-mail-layout';
import type AttachmentPreviewWellView from 'owa-mail-list-store/lib/store/schema/AttachmentPreviewWellView';
import getListViewStore from 'owa-mail-list-store/lib/store/Store';
import { getDensityModeString } from 'owa-fabric-theme';
import React from 'react';
import { plusMoreInlinePreviews } from './MailListItemRichPreviewWell.locstring.json';
import { useMessageListTextStyle } from 'owa-mail-listitem-styles/lib/utils/useMessageListTextStyle';
import classNames from 'owa-classnames';
import shouldHideMessageListCheckboxes from 'owa-messagelist-layout-option/lib/utils/shouldHideMessageListCheckboxes';
import isWin32StylingEnabled from 'owa-theme-win32-style-store/lib/selectors/isWin32StylingEnabled';

import {
    wellHeight,
    oneRowAttachmentPreviews,
    shortWellHeight,
    shortWellLineHeight,
    plusMoreNumber,
} from './MailListItemRichPreviewWell.scss';

import {
    inlinePreviewsSingleLine,
    inlinePreviewsThreeColumn,
    inlinePreviewsThreeColumnFull,
    inlinePreviewsThreeColumnMediumOrCompact,
} from 'owa-mail-listitem-styles/lib/scss/MailListItem.scss';

export interface MailListItemRichPreviewWellProps {
    isWideView: boolean;
    onPreviewClick: (ev: React.MouseEvent<unknown> | React.TouchEvent<unknown>) => void;
    tableViewId: string;
    previewViewState: PreviewWellViewState;
    attachmentPreviewWellViewState: AttachmentPreviewWellView;
}

const defaultExport: React.FunctionComponent<MailListItemRichPreviewWellProps> = observer(
    function MailListItemRichPreviewWell(props: MailListItemRichPreviewWellProps) {
        const densityModeString = getDensityModeString();
        const isFullDensity = densityModeString === 'full';
        const majorMessageListTextStyle = useMessageListTextStyle(
            'Major',
            undefined /* isFirstLine */,
            undefined /* isSingleLineView */,
            isWin32StylingEnabled()
        );

        const renderAttachment = (
            attachmentViewStateId: string,
            isImage: boolean,
            onlyDocsInView: boolean
        ): React.JSX.Element | null => {
            const attachmentViewState =
                getListViewStore().attachmentViewStates.get(attachmentViewStateId);
            if (!attachmentViewState) {
                return null;
            }
            return (
                <AttachmentCompactView
                    onPreviewClick={props.onPreviewClick}
                    key={attachmentViewState.attachmentId.Id}
                    attachmentViewState={attachmentViewState}
                    isImage={isImage}
                    specialDocViewmode={
                        onlyDocsInView ? SpecialDocViewMode.shortDocView : SpecialDocViewMode.none
                    }
                    attachmentViewStateId={attachmentViewStateId}
                    shouldHideFileSize={true}
                />
            );
        };
        const renderAttachments = (
            isImage: boolean,
            attachmentViewStateIds: string[],
            numberToRender: number,
            onlyDocsInView: boolean
        ): React.JSX.Element[] => {
            const renderedAttachments: React.JSX.Element[] = [];
            for (let i = 0; i < numberToRender; i++) {
                const attachment = renderAttachment(
                    attachmentViewStateIds[i],
                    isImage,
                    onlyDocsInView
                );
                if (attachment) {
                    renderedAttachments.push(attachment);
                }
            }
            return renderedAttachments;
        };

        const renderPreviewWell = (
            attachmentPreviewWellViewState: AttachmentPreviewWellView
        ): React.JSX.Element => {
            let nonEmptyPreviewContainerClasses = classNames(
                wellHeight,
                'disableTextSelection',
                oneRowAttachmentPreviews
            );
            const numOfDocs = attachmentPreviewWellViewState.documentViewStateIds.length;
            const numOfImages = attachmentPreviewWellViewState.imageViewStateIds.length;
            const totalNumberOfAttachmentPreviews = numOfDocs + numOfImages;
            // Calculate the number of previews that can be shown and whether to show the plus more div
            const previewViewState: PreviewWellViewState = props.previewViewState;
            const onlyDocsInView = previewViewState.numImageToShow === 0;
            if (onlyDocsInView) {
                nonEmptyPreviewContainerClasses = classNames(
                    shortWellHeight,
                    nonEmptyPreviewContainerClasses
                );
            }

            return (
                <div className={nonEmptyPreviewContainerClasses}>
                    {renderAttachments(
                        false /* isImage */,
                        attachmentPreviewWellViewState.documentViewStateIds,
                        previewViewState.numDocToShow,
                        onlyDocsInView
                    )}
                    {renderAttachments(
                        true /* isImage */,
                        attachmentPreviewWellViewState.imageViewStateIds,
                        previewViewState.numImageToShow,
                        onlyDocsInView
                    )}
                    {previewViewState.shouldShowPlusMore && (
                        <div
                            className={classNames(
                                majorMessageListTextStyle,
                                onlyDocsInView && classNames(shortWellHeight, shortWellLineHeight),
                                plusMoreNumber
                            )}
                        >
                            {format(
                                loc(plusMoreInlinePreviews),
                                totalNumberOfAttachmentPreviews -
                                    previewViewState.numDocToShow -
                                    previewViewState.numImageToShow
                            )}
                        </div>
                    )}
                </div>
            );
        };
        const { isWideView, attachmentPreviewWellViewState } = props;

        const containerDivClasses = classNames(
            'disableTextSelection',
            isWideView
                ? inlinePreviewsSingleLine
                : classNames(
                      inlinePreviewsThreeColumn,
                      isFullDensity
                          ? inlinePreviewsThreeColumnFull
                          : inlinePreviewsThreeColumnMediumOrCompact
                  )
        );

        const previewWellContainerStyle = React.useCallback(() => {
            return {
                marginLeft: getListViewColumnWidths(shouldHideMessageListCheckboxes())
                    .senderColumnWidth,
            };
        }, [props.tableViewId]);

        if (attachmentPreviewWellViewState) {
            return (
                <div
                    className={containerDivClasses}
                    style={isWideView ? previewWellContainerStyle() : undefined}
                >
                    {renderPreviewWell(attachmentPreviewWellViewState)}
                </div>
            );
        }

        return null;
    },
    'MailListItemRichPreviewWell'
);
export default defaultExport;
